import dayjs, { Dayjs } from "dayjs";

import {
  ProducerTask,
  TaskColumn,
} from "../../../../app/services/taskBoard";
import { createMentionRegex, parseMentionAmountHours } from "../../../../utils/text";
import {
  computeTaskLatestDate,
  dateChipRange,
  extractDates,
  isCheckedCheckboxLine,
  isDateInRange,
  maskLinkTargets,
  matchesCompletionFilter,
  type TaskCompletionFilter,
  type TaskPeriodFilter,
} from "./taskBoardUtils";

/**
 * タスクボードの「アクティビティ（作業時間）」集計。
 *
 * セル本文に `@山田[3h]` と書くと、その担当者に 3 時間のアクティビティが計上される。
 * 担当者・完了・期限と同じ「テキスト駆動」の思想で、専用の入力 UI も DB カラムも
 * 持たない（BE は cells の Markdown をそのまま保持するだけ）。
 *
 * 何月分として数えるかは **同じ行に書かれた日付** で決まる。行に日付が無ければ
 * セル全体の最遅日付 → タスクの代表日付 → タスク行の更新日、の順にフォールバック
 * する。1 セルに複数行の予定が書かれていても月が正しく割れるように、走査は行単位
 * で行う。
 *
 * 最後の「更新日」フォールバックがあるため、集計から漏れる「日付なし」は原則
 * 生じない。ただし更新日で埋めた分は推測でしかないので `dateEstimated` を立てて
 * 画面側で日付の追記を促す。
 */

/** 集計 1 件（`@名前[3h]` 1 個ぶん）。 */
export interface ActivityEntry {
  projectId: string;
  projectName: string;
  taskId: string;
  taskTitle: string;
  /**
   * 由来した列の名前（工程名）。タスク名に直接書かれていた場合は null。
   * 列 ID はボードごとに違うため、プロジェクトをまたいで「工程別」に集計
   * できるよう、ID ではなく名前を持つ。
   */
  columnTitle: string | null;
  /** `@` に続く表示名 */
  name: string;
  /** 時間換算の作業量（`[30m]` は 0.5） */
  hours: number;
  /** 帰属日 YYYY-MM-DD。日付も更新日も無い場合のみ null */
  date: string | null;
  /** 帰属月 YYYY-MM。date が null なら null */
  month: string | null;
  /**
   * 日付がどこにも書かれておらず、タスク行の更新日で埋めた場合に true。
   * 集計には含めつつ、画面側で日付の追記を促すために使う。
   */
  dateEstimated: boolean;
  /**
   * 由来した行がチェック済み（`- [x]`）だったか。完了フィルターの対象。
   * チェックボックスを持たない行は、表の完了判定（チェックボックスなし =
   * 未完了）に合わせて false。
   */
  completed: boolean;
}

/** ActivityEntry に添える出所（どのプロジェクトのボードか）の情報。 */
export interface ActivitySource {
  projectId: string;
  projectName: string;
}

/**
 * 1 行のテキストから `@名前[3h]` を抽出する。数量が無い `@名前` は集計対象外
 * （担当者フィルターには従来どおり効くが、時間は 0 として扱わず行に載せない）。
 *
 * 同じ行に同じ担当者が複数回現れた場合は、それぞれ別の要素として返す
 * （`@山田[2h] @山田[1h]` は合計 3h になる）。
 */
export function extractActivityMentions(
  text: string,
): { name: string; hours: number }[] {
  if (!text) return [];
  const result: { name: string; hours: number }[] = [];
  for (const m of maskLinkTargets(text).masked.matchAll(createMentionRegex())) {
    const name = m[2]?.trim();
    const hours = parseMentionAmountHours(m[3], m[4]);
    if (name && hours != null) result.push({ name, hours });
  }
  return result;
}

/** テキスト中で最も遅い日付を YYYY-MM-DD で返す（無ければ null） */
function latestDateOf(text: string, base: Dayjs): string | null {
  const dates = extractDates(text, base);
  return dates.length > 0 ? dates[dates.length - 1].format("YYYY-MM-DD") : null;
}

/**
 * タスク 1 件（タスク名 + 全セル）からアクティビティを抽出する。
 * `columns` に無いキーのセルは表に出ないため対象外にする。
 */
export function collectTaskActivities(
  task: ProducerTask,
  columns: TaskColumn[],
  source: ActivitySource,
  base: Dayjs = dayjs(),
): ActivityEntry[] {
  const result: ActivityEntry[] = [];
  const writtenTaskDate = computeTaskLatestDate(task, base);
  // 本文のどこにも日付が無いときの最終フォールバック。集計から漏れる
  // 「日付なし」を作らないため、その行が最後に触られた日を帰属日にする。
  const updatedDate = task.modified
    ? dayjs(task.modified).format("YYYY-MM-DD")
    : null;

  const push = (
    column: TaskColumn | null,
    line: string,
    fallbackDate: string | null,
  ) => {
    const mentions = extractActivityMentions(line);
    if (mentions.length === 0) return;
    const written = latestDateOf(line, base) ?? fallbackDate;
    const date = written ?? updatedDate;
    // 完了は行単位（その行のチェックボックスがチェック済みか）で判定する
    const completed = isCheckedCheckboxLine(line);
    for (const { name, hours } of mentions) {
      result.push({
        ...source,
        taskId: task.id,
        taskTitle: task.title || "",
        columnTitle: column?.title || null,
        name,
        hours,
        date,
        month: date ? date.slice(0, 7) : null,
        dateEstimated: written == null && date != null,
        completed,
      });
    }
  };

  push(null, task.title || "", writtenTaskDate);

  for (const column of columns) {
    const content = task.cells?.[column.id]?.content ?? "";
    if (!content) continue;
    // セル内に日付があれば、日付を持たない行はそのセルの日付に寄せる
    const cellFallbackDate = latestDateOf(content, base) ?? writtenTaskDate;
    for (const line of content.split("\n")) {
      push(column, line, cellFallbackDate);
    }
  }

  return result;
}

// ── 集計 ────────────────────────────────────────────────────────────

/** 帯グラフ 1 本ぶん。`segments` は内訳（積み上げの各区間）。 */
export interface ActivityBucket {
  key: string;
  label: string;
  hours: number;
  segments: { key: string; label: string; hours: number }[];
}

/** 合計時間 */
export function sumActivityHours(entries: ActivityEntry[]): number {
  return entries.reduce((total, e) => total + e.hours, 0);
}

/**
 * 共通の期間フィルター（日付チップ + 月次）で絞り込む。null = すべて（素通し）。
 * - 月（`YYYY-MM`、期間チップの先月 / 今月 / 来月）は帰属月が一致するもののみ。
 * - 日付チップ（先週 / 本日 / 明日 / 今週）は帰属日がその範囲に入るもののみ
 *   （帰属日を持たないアクティビティは対象外）。
 */
export function filterActivitiesByPeriod(
  entries: ActivityEntry[],
  period: TaskPeriodFilter | null,
  base: Dayjs = dayjs(),
): ActivityEntry[] {
  if (!period) return entries;
  if (period.kind === "month") {
    return entries.filter((e) => e.month === period.month);
  }
  const range = dateChipRange(period.chip, base);
  return entries.filter((e) => !!e.date && isDateInRange(dayjs(e.date), range));
}

/**
 * 完了フィルター（すべて / 未完了 / 完了）で絞り込む。
 * 判定は行単位（{@link ActivityEntry.completed}）。チェック済みの行 = 実績、
 * 未チェックの行 = 予定、として使い分けられる。
 */
export function filterActivitiesByCompletion(
  entries: ActivityEntry[],
  filter: TaskCompletionFilter,
): ActivityEntry[] {
  if (filter === "all") return entries;
  return entries.filter((e) => matchesCompletionFilter(e.completed, filter));
}

/**
 * 2 段のキーで集計して帯グラフ用のバケットにする。
 * 外側キーが 1 本の帯、内側キーが積み上げの各区間になる。
 * 帯・区間とも合計時間の降順。
 */
function aggregate(
  entries: ActivityEntry[],
  // eslint-disable-next-line no-unused-vars
  outer: (entry: ActivityEntry) => { key: string; label: string },
  // eslint-disable-next-line no-unused-vars
  inner: (entry: ActivityEntry) => { key: string; label: string },
): ActivityBucket[] {
  const buckets = new Map<
    string,
    { label: string; hours: number; segments: Map<string, { label: string; hours: number }> }
  >();

  for (const entry of entries) {
    const o = outer(entry);
    let bucket = buckets.get(o.key);
    if (!bucket) {
      bucket = { label: o.label, hours: 0, segments: new Map() };
      buckets.set(o.key, bucket);
    }
    bucket.hours += entry.hours;

    const i = inner(entry);
    const segment = bucket.segments.get(i.key);
    if (segment) segment.hours += entry.hours;
    else bucket.segments.set(i.key, { label: i.label, hours: entry.hours });
  }

  // 合計が同じときは名前順にして、再集計のたびに並びが入れ替わらないようにする
  const byHoursThenLabel = (
    a: { label: string; hours: number },
    b: { label: string; hours: number },
  ) => b.hours - a.hours || a.label.localeCompare(b.label, "ja");

  return [...buckets.entries()]
    .map(([key, bucket]) => ({
      key,
      label: bucket.label,
      hours: bucket.hours,
      segments: [...bucket.segments.entries()]
        .map(([segKey, seg]) => ({ key: segKey, label: seg.label, hours: seg.hours }))
        .sort(byHoursThenLabel),
    }))
    .sort(byHoursThenLabel);
}

/** 列（工程）が特定できないアクティビティ（タスク名に直接書かれたもの）の表示名 */
export const NO_COLUMN_LABEL = "タスク名";

/** 集計の切り口 */
export type ActivityDimension = "project" | "staff" | "column";

/**
 * 切り口ごとのキーと表示名。工程は列 ID がボードごとに違うため、プロジェクトを
 * またいで同じ工程を合算できるよう**列名**をキーにする。
 */
const DIMENSIONS: Record<
  ActivityDimension,
  // eslint-disable-next-line no-unused-vars
  (entry: ActivityEntry) => { key: string; label: string }
> = {
  project: (e) => ({ key: e.projectId, label: e.projectName }),
  staff: (e) => ({ key: e.name, label: e.name }),
  column: (e) => {
    const title = e.columnTitle || NO_COLUMN_LABEL;
    return { key: title, label: title };
  },
};

/**
 * `outer` 単位で 1 本ずつ帯を作り、各帯の内訳を `inner` の単位で切る。
 * 期間内に実績のある `outer` だけが並ぶ（0 件のものは行を作らない）。
 */
export function aggregateBy(
  entries: ActivityEntry[],
  outer: ActivityDimension,
  inner: ActivityDimension,
): ActivityBucket[] {
  return aggregate(entries, DIMENSIONS[outer], DIMENSIONS[inner]);
}
